import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faChevronRight,
  faCaretDown,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/Header.scss";
import { useDrawer } from "../contexts/DrawerContext";
import TooltipComponent from "./tooltipComponent";

const Breadcrumbs = ({ breadcrumbData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();

  const rawPathnames = location.pathname.split("/").filter((x) => x);
  const baseSegment = rawPathnames[0];
  const pathnames = rawPathnames.filter(
    (x, idx) =>
      !(
        idx === 0 &&
        (x === "courses" || x === "materials" || x === "question-bank")
      ),
  );

  const getKey = (obj) =>
    Object.keys(obj).find((k) => k !== "next_count" && k !== "page_count");

  const getTitleFromData = (key, index) => {
    if (!breadcrumbData) return null;

    if (index === 0 && breadcrumbData.course_name) {
      return breadcrumbData.course_name;
    }

    if (index === 1 && breadcrumbData.subject) {
      const subject = breadcrumbData.subject.find(
        (s) => s[key] && typeof s[key] === "string",
      );
      return subject?.[key];
    }

    if (index === 2 && breadcrumbData.topic) {
      const topicGroup = breadcrumbData.topic.find((g) =>
        Object.values(g).some(
          (topics) =>
            Array.isArray(topics) &&
            topics.some((t) => t[key] && typeof t[key] === "string"),
        ),
      );
      if (!topicGroup) return null;

      const topics = Object.values(topicGroup).flat();
      const topic = topics.find((t) => t[key] && typeof t[key] === "string");
      return topic?.[key];
    }

    if (index === 3 && breadcrumbData.subtopic) {
      const subtopicGroup = breadcrumbData.subtopic.find((g) =>
        Object.values(g).some(
          (subtopics) =>
            Array.isArray(subtopics) &&
            subtopics.some((t) => t[key] && typeof t[key] === "string"),
        ),
      );
      if (!subtopicGroup) return null;

      const subtopics = Object.values(subtopicGroup).flat();
      const subtopic = subtopics.find(
        (t) => t[key] && typeof t[key] === "string",
      );
      return subtopic?.[key];
    }

    return null;
  };

  const formatName = (slug) => {
    return slug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getDisplayName = (segment, index) => {
    const title = getTitleFromData(segment, index);
    return title || formatName(segment);
  };

  const handleDrawerOpen = (index) => {
    if (!breadcrumbData) return;

    let items = [];
    let title = "";
    let subtitle = "";

    const basePath = `/${baseSegment}/${pathnames.slice(0, index).join("/")}`;
    const courseName = breadcrumbData.course_name || "Course";
    let subjectName = "";

    if (index >= 1 && pathnames[1]) {
      const subjectKey = pathnames[1];
      const subjectObj = breadcrumbData.subject?.find((s) => s[subjectKey]);
      if (subjectObj) {
        subjectName = subjectObj[subjectKey];
      }
    }

    if (index === 1) {
      title = "Select Subject";
      subtitle = "Make more personalise and accurate by selecting subject";
      if (breadcrumbData.subject) {
        items = breadcrumbData.subject.map((subjectObj) => {
          const key = getKey(subjectObj);
          return {
            id: key,
            title: subjectObj[key],
            subtitle: `${subjectObj.next_count || 0} topics`,
            path: `/courses/${pathnames[0]}/${key}`,
            children: [],
          };
        });
      }
    } else if (index === 2) {
      title = "Select Topic";
      subtitle = "Make more personalise and accurate by selecting topic";
      const subjectKey = pathnames[1];
      const topicGroup = breadcrumbData.topic?.find((g) => g[subjectKey]);
      if (topicGroup && topicGroup[subjectKey]) {
        items = topicGroup[subjectKey].map((topicObj) => {
          const key = getKey(topicObj);
          return {
            id: key,
            title: topicObj[key],
            subtitle: `${topicObj.next_count || 0} subtopics`,
            path: `${basePath}/${key}`,
            children: [],
          };
        });
      }
    } else if (index === 3) {
      title = "Select Subtopic";
      subtitle = "Make more personalise and accurate by selecting subtopic";
      const topicKey = pathnames[2];
      const subtopicGroup = breadcrumbData.subtopic?.find((g) => g[topicKey]);
      if (subtopicGroup && subtopicGroup[topicKey]) {
        items = subtopicGroup[topicKey].map((subtopicObj) => {
          const key = getKey(subtopicObj);
          const count =
            subtopicObj.next_count !== undefined
              ? subtopicObj.next_count
              : subtopicObj.page_count || 0;
          const label =
            baseSegment === "question-bank" ? "questions" : "Pages";
          return {
            id: key,
            title: subtopicObj[key],
            subtitle: `${count} ${label}`,
            path: `${basePath}/${key}`,
            children: [],
          };
        });
      }
    }

    const currentKey = pathnames[index];
    const selectedId = pathnames[index + 1] || currentKey || null;

    if (items.length === 0 && currentKey) {
      items = [
        {
          id: currentKey,
          title: getDisplayName(currentKey, index),
          subtitle: "Current selection",
          path: location.pathname,
          children: [],
        },
      ];
    }

    openDrawer({
      title,
      subtitle:
        items.length === 0 ? "No items available at this level" : subtitle,
      items,
      onItemClick: (item) => {
        if (item.path && item.path !== location.pathname) {
          navigate(item.path);
        }
      },
      courseName,
      subjectName,
      selectedId,
    });
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (pathnames.length === 0) {
    return null;
  }

  if (isMobile) {
    const currentIndex = pathnames.length - 1;
    const currentName = getDisplayName(pathnames[currentIndex], currentIndex);
    const parentPath =
      pathnames.length > 1
        ? `/${baseSegment}/${pathnames.slice(0, currentIndex).join("/")}`
        : "/dashboard";

    const showMobileCaret = currentIndex >= 1 && breadcrumbData;

    return (
      <div className="breadcrumbs breadcrumbs--mobile">
        <Link to={parentPath} className="breadcrumbs__back-icon">
          <FontAwesomeIcon icon={faChevronRight} rotation={180} />
        </Link>
        <div
          className="breadcrumbs__current-title"
          onClick={() => showMobileCaret && handleDrawerOpen(currentIndex)}
          style={{ cursor: showMobileCaret ? "pointer" : "default" }}
        >
          <TooltipComponent maxWidth={100}>{currentName}</TooltipComponent>
          {showMobileCaret && (
            <FontAwesomeIcon
              icon={faCaretDown}
              size="xs"
              className="breadcrumbs__caret"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="breadcrumbs">
      {(() => {
        let homePath = "/dashboard";

        if (baseSegment === "courses" && pathnames[0]) {
          homePath = `/courses/${pathnames[0]}`;
        }

        return (
          <Link to={homePath} className="breadcrumbs__item breadcrumbs__home">
            <FontAwesomeIcon icon={faHouse} />
          </Link>
        );
      })()}
      {pathnames.map((value, index) => {
        const to = `/${baseSegment}/${pathnames.slice(0, index + 1).join("/")}`;
        const isLast = index === pathnames.length - 1;
        const displayName = getDisplayName(value, index);

        const showDropdown = index >= 1 && index <= 3 && breadcrumbData;

        return (
          <React.Fragment key={to}>
            <span className="breadcrumbs__separator">
              <FontAwesomeIcon icon={faChevronRight} size="xs" />
            </span>
            <div className="breadcrumbs__item-wrapper">
              {isLast ? (
                <span className="breadcrumbs__item breadcrumbs__item--active">
                  <TooltipComponent maxWidth={90}>
                    {displayName}
                  </TooltipComponent>
                </span>
              ) : (
                <Link to={to} className="breadcrumbs__item">
                  <TooltipComponent maxWidth={90}>
                    {displayName}
                  </TooltipComponent>
                </Link>
              )}
              {showDropdown && (
                <button
                  className="breadcrumb-dropdown__trigger"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDrawerOpen(index);
                  }}
                >
                  <FontAwesomeIcon icon={faCaretDown} size="xs" />
                </button>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumbs;
